import { useState } from "react";
import { useData } from "@/hooks/useData";
import { IPMAStation } from "@/types/ipma-station/ipma-station.types";
import { useIPMAStationFilters } from "./useIPMAStationFilters";
import { InformationSource } from "@/types/information-source/information-source.types";
import { useMemo } from "react";

interface UseIPMAStationTableProps {
  basins?: { id: string; name: string }[];
}

export const useIPMAStationTable = ({ basins = [] }: UseIPMAStationTableProps = {}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [basinFilters, setBasinFilters] = useState<string[]>([]);

  const {
    data: stations,
    loading: stationsLoading,
    refetch: refetchStations,
  } = useData<IPMAStation>({
    collectionName: "setup_ipmaStations",
  });

  const {
    data: infoSources,
    loading: sourcesLoading,
  } = useData<InformationSource>({
    collectionName: "setup_infoSources",
  });

  const { filteredStations, getUniqueOptions } = useIPMAStationFilters({
    stations,
    searchTerm,
    showArchived,
    basinFilters,
    basins,
  });

  // Create a map of information source IDs to display names
  const infoSourceMap = useMemo(() => {
    if (!infoSources) return {};
    
    return infoSources.reduce<Record<string, string>>((acc, source) => {
      acc[source.id] = `${source.name} (${source.year})`;
      return acc;
    }, {});
  }, [infoSources]);

  const clearFilters = () => {
    setSearchTerm("");
    setBasinFilters([]);
  };

  const basinOptions = getUniqueOptions("basin");
  const loading = stationsLoading || sourcesLoading;

  return {
    stations,
    filteredStations,
    loading,
    refetch: refetchStations,
    searchTerm,
    setSearchTerm,
    showArchived,
    setShowArchived,
    basinFilters,
    setBasinFilters,
    clearFilters,
    basinOptions,
    infoSourceMap,
  };
}; 